import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { API_BASE, AUTH_AUDIENCE } from '../config/env';

type RequestInitWithAuth = RequestInit & { skipAuth?: boolean };

class AuthRedirectRequiredError extends Error {
  constructor() {
    super('AUTH_REDIRECT_REQUIRED');
    this.name = 'AuthRedirectRequiredError';
  }
}

class AuthHydrationPendingError extends Error {
  constructor() {
    super('AUTH_HYDRATION_PENDING');
    this.name = 'AuthHydrationPendingError';
  }
}

let authRedirectInFlight: Promise<void> | null = null;

function queueAuthRedirect(loginWithRedirect: (options?: { appState?: { returnTo?: string } }) => Promise<void>) {
  if (!authRedirectInFlight) {
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    authRedirectInFlight = loginWithRedirect({ appState: { returnTo } })
      .catch(() => {})
      .finally(() => {
        authRedirectInFlight = null;
      });
  }
  return authRedirectInFlight;
}

export function useApiClient() {
  const { getAccessTokenSilently, loginWithRedirect, isLoading } = useAuth0();

  const getToken = useCallback(async () => {
    if (isLoading) {
      throw new AuthHydrationPendingError();
    }

    try {
      return await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
    } catch (err: any) {
      if (err?.error === 'login_required' || err?.error === 'missing_refresh_token') {
        await queueAuthRedirect(loginWithRedirect);
        throw new AuthRedirectRequiredError();
      }
      throw err;
    }
  }, [getAccessTokenSilently, isLoading, loginWithRedirect]);

  const fetchApi = useCallback(
    async (path: string, init: RequestInitWithAuth = {}) => {
      const token = init.skipAuth ? '' : await getToken();
      const res = await fetch(`${API_BASE}${path}`, {
        ...init,
        headers: {
          ...(init.headers || {}),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });
      return res;
    },
    [getToken],
  );

  const fetchJson = useCallback(
    async <T>(path: string, init?: RequestInitWithAuth) => {
      const res = await fetchApi(path, init);
      if (!res.ok) {
        const bodyText = await res.text();
        if (!bodyText) throw new Error(`Request failed ${res.status}`);
        let parsed:
          | {
              message?: string;
              error?: string | { message?: string };
            }
          | null = null;
        try {
          parsed = JSON.parse(bodyText) as {
            message?: string;
            error?: string | { message?: string };
          };
        } catch {
          parsed = null;
        }
        const nestedError =
          parsed?.error && typeof parsed.error === 'object' ? parsed.error.message : parsed?.error;
        const message = parsed?.message || nestedError || bodyText || `Request failed ${res.status}`;
        throw new Error(String(message));
      }
      return (await res.json()) as T;
    },
    [fetchApi],
  );

  return { fetchApi, fetchJson, getToken };
}
